/**
 * Per-tenant Stripe Connect + pay-by-invoice API client (2026-06-27).
 *
 * Authed, owner-gated endpoints under /api/account/payments/* let an owner connect their OWN Stripe
 * account (Standard Connect, OAuth) so customers pay invoices into the org's account directly, and
 * configure the public pay-by-invoice page (slug + optional custom domain).
 *
 * Shapes come from @beam/api-types. No card data or keys ever cross this boundary (mobile invariant
 * 2) — only the connected account id, capability flags, and the public pay-page config. NEVER
 * imports @smartbox/* (invariant 1).
 */
import {
  ConnectStatus,
  ConnectBeginResponse,
  PayPageConfig,
  DomainVerifyResponse,
} from '@beam/api-types';
import type {
  ConnectStatus as ConnectStatusType,
  PayPageConfig as PayPageConfigType,
  PayPageConfigInput,
  DomainVerifyResponse as DomainVerifyResponseType,
} from '@beam/api-types';
import { get, post, put } from './client';

export async function getPaymentsStatus(): Promise<ConnectStatusType> {
  return ConnectStatus.parse(await get<unknown>('/api/account/payments/status'));
}

/** Begin OAuth — returns the Stripe authorize URL the app opens in a browser. */
export async function beginConnect(): Promise<{ url: string }> {
  return ConnectBeginResponse.parse(await post<unknown>('/api/account/payments/connect'));
}

export async function disconnectStripe(): Promise<{ ok: true }> {
  await post<unknown>('/api/account/payments/disconnect');
  return { ok: true };
}

export async function setPayPage(input: PayPageConfigInput): Promise<PayPageConfigType> {
  return PayPageConfig.parse(await put<unknown>('/api/account/payments/pay-page', input));
}

export async function verifyPayDomain(): Promise<DomainVerifyResponseType> {
  return DomainVerifyResponse.parse(await post<unknown>('/api/account/payments/pay-page/verify'));
}
